import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS, BrandXXS } from '@sorare/core/src/atoms/typography';
import { PositionIcon } from '@sorare/core/src/components/lineup/PositionIcon';

import { SkinIcon } from '@sorare/core/src/components/collections/SkinIcon/index.tsx';
import type { PlaceholderSlot_userCardCollectionSlot } from './__generated__/index.graphql.ts';

const CardWrapper = styled(Vertical).attrs({ center: true })`
  border-radius: var(--unit);
  position: relative;
  border: 1px solid var(--c-nd-50);
  gap: var(--unit);
  height: 100%;
  aspect-ratio: var(--card-aspect-ratio);
  & > svg {
    color: var(--c-nd-600);
  }
`;

const SkinIconWrapper = styled.div`
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
`;

type Props = {
  slot: Nullable<PlaceholderSlot_userCardCollectionSlot>;
  small?: boolean;
  displayPlayerName?: boolean;
};

export const PlaceholderSlot = ({ slot, small, displayPlayerName }: Props) => {
  const { slot: collectionSlot, nomenclature, skinEdition } = slot || {};
  const { displayName } = collectionSlot?.anyPlayer || {};
  const position = collectionSlot?.anyPlayer?.anyPositions?.[0];

  const Label = small ? BrandXXS : BrandXS;
  return (
    <CardWrapper>
      {position ? (
        <PositionIcon position={position} size={small ? 1.5 : 2} />
      ) : null}
      <Label bold color="var(--c-nd-600)">
        {nomenclature}
      </Label>

      {!!displayPlayerName && (
        <Label bold color="var(--c-nd-600)" className="text-center">
          {displayName}
        </Label>
      )}
      <SkinIconWrapper>
        <SkinIcon skin={skinEdition?.skin} size={2} />
      </SkinIconWrapper>
    </CardWrapper>
  );
};

PlaceholderSlot.fragments = {
  userCardCollectionSlot: gql`
    fragment PlaceholderSlot_userCardCollectionSlot on UserCardCollectionSlot {
      slug
      skinEdition {
        skin
      }
      nomenclature
      slot {
        id
        anyPlayer {
          slug
          anyPositions
          displayName
        }
      }
    }
  ` as TypedDocumentNode<PlaceholderSlot_userCardCollectionSlot>,
};
